import React, { useState, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import ScrollToTop from './components/ScrollToTop';
import LanguageWrapper from './components/LanguageWrapper';
import { useLangPrefix } from './hooks/useLocalePath';
import { SUPPORTED_LANGS } from './i18n';

/* ── Eagerly loaded: Landing + Auth (critical first-paint) ── */
import Login from './pages/Login';
import Register from './pages/Register';
import Landing from './pages/Landing';

/* ── Lazy loaded: app pages (behind auth) ── */
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Projects = lazy(() => import('./pages/Projects'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const ReviewSession = lazy(() => import('./pages/ReviewSession'));
const Billing = lazy(() => import('./pages/Billing'));
const ApiKeys = lazy(() => import('./pages/ApiKeys'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const InviteAccept = lazy(() => import('./pages/InviteAccept'));
const MyAssignments = lazy(() => import('./pages/MyAssignments'));
const MyQA = lazy(() => import('./pages/MyQA'));
import OnboardingTour from './components/OnboardingTour'; // eager: survit aux navigations lazy (Suspense)
const BlogArticle = lazy(() => import('./pages/BlogArticles'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));

/* ── Heavy content pages: lazy with named-export wrappers ── */
const CGU = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.CGU })));
const Privacy = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.Privacy })));
const RGPD = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.RGPD })));
const Contact = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.Contact })));
const About = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.About })));
const Changelog = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.Changelog })));
const Roadmap = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.Roadmap })));
const Documentation = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.Documentation })));
const BlogPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.BlogPage })));
const Guides = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.Guides })));
const StatusPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.StatusPage })));
const UserDocumentation = lazy(() => import('./pages/DocsPages').then(m => ({ default: m.UserDocumentation })));
const ApiDocs = lazy(() => import('./pages/DocsPages').then(m => ({ default: m.ApiDocs })));
const GuideDemarrer = lazy(() => import('./pages/GuidesPages').then(m => ({ default: m.GuideDemarrer })));
const GuideAnnotations = lazy(() => import('./pages/GuidesPages').then(m => ({ default: m.GuideAnnotations })));
const GuideEquipe = lazy(() => import('./pages/GuidesPages').then(m => ({ default: m.GuideEquipe })));
const GuideWorkflow = lazy(() => import('./pages/GuidesPages').then(m => ({ default: m.GuideWorkflow })));
const GuideApi = lazy(() => import('./pages/GuidesPages').then(m => ({ default: m.GuideApi })));
const GuideAnalytics = lazy(() => import('./pages/GuidesPages').then(m => ({ default: m.GuideAnalytics })));

const LoadingFallback = () => (
  <div className="loading-screen"><div className="spinner" /></div>
);

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const langPrefix = useLangPrefix();
  if (loading) return <LoadingFallback />;
  return user ? children : <Navigate to={`${langPrefix}/login`} />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const langPrefix = useLangPrefix();
  if (loading) return <LoadingFallback />;
  return user?.role === 'admin' ? children : <Navigate to={`${langPrefix}/dashboard`} />;
};

/* ── Trial banner for free users ── */
const TrialBanner = () => {
  const { trialInfo } = useAuth();
  const { t } = useTranslation();
  const langPrefix = useLangPrefix();
  const [dismissed, setDismissed] = useState(false);
  if (!trialInfo.isFree || dismissed) return null;
  const isExpired = trialInfo.isExpired;
  const bg = isExpired
    ? 'linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)'
    : 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)';
  return (
    <div style={{
      background: bg,
      color: '#fff',
      padding: '0.45rem 1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.75rem',
      fontSize: '0.82rem',
      fontWeight: 500,
      position: 'fixed',
      top: 0,
      left: 'var(--sidebar-width, 260px)',
      right: 0,
      zIndex: 150,
      textAlign: 'center',
    }}>
      {isExpired ? (
        <>
          <span>{t('trialBanner.expired')}</span>
          <Link to={`${langPrefix}/billing`} style={{ color: '#fff', fontWeight: 700, textDecoration: 'underline' }}>
            {t('trialBanner.choosePlan')}
          </Link>
        </>
      ) : (
        <>
          <span>
            {trialInfo.daysLeft != null
              ? t('trialBanner.trialDaysLeft', { days: trialInfo.daysLeft })
              : t('trialBanner.trialHoursLeft', { hours: trialInfo.hoursLeft || 0 })
            }
          </span>
          <Link to={`${langPrefix}/billing`} style={{ color: '#fff', fontWeight: 700, textDecoration: 'underline' }}>
            {t('trialBanner.seePlans')}
          </Link>
          <button
            onClick={() => setDismissed(true)}
            style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
              cursor: 'pointer', fontSize: '1rem', padding: '0 0.25rem', marginLeft: 'auto'
            }}
          >✕</button>
        </>
      )}
    </div>
  );
};

// Non-default language codes (e.g. ['en']) — used to strip prefixes from paths
const NON_DEFAULT_CODES = SUPPORTED_LANGS.filter((l) => !l.isDefault).map((l) => l.code);

/* ── All app routes (shared between all language trees) ── */
const AppRoutes = () => {
  const { user } = useAuth();
  const location = useLocation();
  const langPrefix = useLangPrefix();
  const [tourDismissed, setTourDismissed] = useState(false);
  const isReviewPage = location.pathname.match(/\/review\//);
  const publicPaths = ['/', '/login', '/register', '/cgu', '/confidentialite', '/rgpd',
    '/contact', '/a-propos', '/changelog', '/roadmap', '/documentation', '/blog', '/guides', '/status', '/docs', '/api-docs',
    '/guides/demarrer', '/guides/annotations', '/guides/equipe', '/guides/workflow', '/guides/api', '/guides/analytics', '/faq', '/vs/bugherd', '/vs/markerio', '/vs/pastel'];
  const isPublicBlog = location.pathname.match(/\/blog\//);
  // Strip any non-default language prefix to get the bare path
  const langPrefixRegex = new RegExp(`^\/(${NON_DEFAULT_CODES.join('|')})`);
  const cleanPath = location.pathname.replace(langPrefixRegex, '') || '/';
  const isPublicPage = publicPaths.includes(cleanPath) || location.pathname.includes('/invite/') || isPublicBlog;
  const showSidebar = user && !isReviewPage && !isPublicPage;
  const showTour = user && !tourDismissed && !user.has_seen_tour && showSidebar;
  return (
    <>
      {showSidebar && <TrialBanner />}
      {showSidebar && <Navbar />}
      {showTour && (
        <Suspense fallback={null}>
          <OnboardingTour onComplete={() => setTourDismissed(true)} />
        </Suspense>
      )}
      <main className={showSidebar ? 'main-content' : ''}>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={user ? <Navigate to={`${langPrefix}/dashboard`} /> : <Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/invite/:token" element={<InviteAccept />} />
            <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/projects" element={<PrivateRoute><Projects /></PrivateRoute>} />
            <Route path="/projects/:id" element={<PrivateRoute><ProjectDetail /></PrivateRoute>} />
            <Route path="/review/:sessionId" element={<PrivateRoute><ReviewSession /></PrivateRoute>} />
            <Route path="/assignments" element={<PrivateRoute><MyAssignments /></PrivateRoute>} />
            <Route path="/qa" element={<PrivateRoute><MyQA /></PrivateRoute>} />
            <Route path="/billing" element={<PrivateRoute><Billing /></PrivateRoute>} />
            <Route path="/api-keys" element={<PrivateRoute><ApiKeys /></PrivateRoute>} />
            <Route path="/cgu" element={<CGU />} />
            <Route path="/confidentialite" element={<Privacy />} />
            <Route path="/rgpd" element={<RGPD />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/a-propos" element={<About />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/roadmap" element={<Roadmap />} />
            <Route path="/documentation" element={<Documentation />} />
            <Route path="/docs" element={<UserDocumentation />} />
            <Route path="/api-docs" element={<ApiDocs />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/guides" element={<Guides />} />
            <Route path="/guides/demarrer" element={<GuideDemarrer />} />
            <Route path="/guides/annotations" element={<GuideAnnotations />} />
            <Route path="/guides/equipe" element={<GuideEquipe />} />
            <Route path="/guides/workflow" element={<GuideWorkflow />} />
            <Route path="/guides/api" element={<GuideApi />} />
            <Route path="/guides/analytics" element={<GuideAnalytics />} />
            <Route path="/faq" element={<FAQPage />} />             <Route path="/vs/:competitor" element={<ComparePage />} />             <Route path="/status" element={<StatusPage />} />
            <Route path="/admin/*" element={<AdminRoute><AdminPanel /></AdminRoute>} />
            <Route path="*" element={<Navigate to={user ? `${langPrefix}/dashboard` : `${langPrefix}/`} />} />
          </Routes>
        </Suspense>
      </main>
    </>
  );
};

export default function App() {
  return (
    <div className="app">
      <ScrollToTop />
      <Routes>
        {/* Non-default languages: /:langCode/... (generated from SUPPORTED_LANGS) */}
        {SUPPORTED_LANGS.filter((l) => !l.isDefault).map((l) => (
          <Route key={l.code} path={`/${l.code}/*`} element={
            <LanguageWrapper>
              <AppRoutes />
            </LanguageWrapper>
          } />
        ))}
        {/* Default language (FR): /... */}
        <Route path="/*" element={
          <LanguageWrapper>
            <AppRoutes />
          </LanguageWrapper>
        } />
      </Routes>
    </div>
  );
}
